/**
 * OnboardingProgress — Stripe/Vercel-style "Step X of Y" header during a
 * new user's first session (V1-11 / Nav 5).
 *
 * Renders only while the user is mid-onboarding. Once they complete all 4
 * steps (key, resume, first save, first tailor), this component returns
 * null and never appears again — there's no manual dismiss because we
 * derive state from the backend, not localStorage.
 *
 * The 4 steps map to user state booleans:
 *   1. Connect Anthropic key      → has_key
 *   2. Upload resume              → has_resume
 *   3. Save your first job        → has_applications
 *   4. Tailor your first resume   → tailored_count > 0
 *
 * "Activated" = all 4 = true. We hide as soon as activated to keep the
 * surface clean for returning users.
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box, LinearProgress, Typography, Stack, Tooltip, Container,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { useAuth } from '../context/AuthContext';
import { getUserState } from '../services/api';

const STEP_KEYS = [
  { key: 'connect_key',   field: 'has_key' },
  { key: 'upload_resume', field: 'has_resume' },
  { key: 'first_save',    field: 'has_applications' },
  { key: 'first_tailor',  field: 'tailored_count_positive' },
];

export default function OnboardingProgress() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [state, setState] = useState(null);

  useEffect(() => {
    if (!user) { setState(null); return; }
    let cancelled = false;
    getUserState()
      .then((res) => { if (!cancelled) setState(res.data); })
      .catch(() => { if (!cancelled) setState(null); });
    return () => { cancelled = true; };
  }, [user]);

  // Don't render at all if we have no data, no user, or the user is
  // fully activated. Keeps the surface clean for returning users —
  // "established" mode shows zero onboarding chrome.
  if (!user || !state) return null;

  // Derive boolean for tailored_count > 0 so we can use the same shape.
  const completion = {
    has_key: state.has_key,
    has_resume: state.has_resume,
    has_applications: state.has_applications,
    tailored_count_positive: (state.tailored_count || 0) > 0,
  };
  const completedCount = STEP_KEYS.filter(s => completion[s.field]).length;
  const totalSteps = STEP_KEYS.length;

  // Activated → hide. This is the most important early-return: returning
  // users should see nothing here.
  if (completedCount >= totalSteps) return null;

  const progressPct = Math.round((completedCount / totalSteps) * 100);
  const currentStepIndex = STEP_KEYS.findIndex(s => !completion[s.field]);
  const currentStepLabel = currentStepIndex >= 0
    ? t(`onboarding.step_${STEP_KEYS[currentStepIndex].key}`)
    : '';

  return (
    <Box
      sx={{
        bgcolor: 'primary.50',
        borderBottom: '1px solid',
        borderColor: 'primary.light',
      }}
    >
      <Container maxWidth="md" sx={{ py: 1.25 }}>
        <Stack direction="row" alignItems="center" spacing={2}>
          {/* Step counter — readable at a glance */}
          <Typography
            variant="caption"
            color="primary.dark"
            fontWeight={600}
            sx={{ whiteSpace: 'nowrap', fontSize: 12 }}
          >
            {t('onboarding.step_of', { current: completedCount + 1, total: totalSteps })}
          </Typography>

          {/* Current step label — what's next */}
          <Typography
            variant="body2"
            sx={{
              flex: 1,
              fontSize: 13,
              color: 'text.primary',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {currentStepLabel}
          </Typography>

          {/* Step dots — checked or unchecked. Tooltip explains each. */}
          <Stack direction="row" spacing={0.5} sx={{ display: { xs: 'none', sm: 'flex' } }}>
            {STEP_KEYS.map((step) => {
              const done = completion[step.field];
              return (
                <Tooltip key={step.key} title={t(`onboarding.step_${step.key}`)} arrow>
                  {done
                    ? <CheckCircleIcon sx={{ fontSize: 18, color: 'success.main' }} />
                    : <RadioButtonUncheckedIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
                  }
                </Tooltip>
              );
            })}
          </Stack>
        </Stack>

        {/* Linear progress bar — visual reinforcement of step counter */}
        <LinearProgress
          variant="determinate"
          value={progressPct}
          sx={{
            mt: 1,
            height: 4,
            borderRadius: 2,
            bgcolor: 'rgba(10,37,64,0.08)',
            '& .MuiLinearProgress-bar': { borderRadius: 2 },
          }}
        />
      </Container>
    </Box>
  );
}
